// AvisoAseosMismoDia — advertencia «esta aseadora ya tiene otro aseo ese día».
// Round 2026-09-11, pedido del owner: que dos aseos de una misma persona no se
// topen. Es un AVISO, no un bloqueo: una aseadora puede hacer dos aseos en un
// día a horas distintas, y la decisión es del admin.
//
// Lo usan los dos modales que asignan aseadoras (/aseos y el del calendario):
//   • <AvisoAseosMismoDia>: aviso EN VIVO bajo el selector de aseadoras. Lo
//     monta RepartoAseadorasField (compartido por los dos modales) cuando el
//     modal le pasa `avisoMismoDia`.
//   • confirmarAseosMismoDia(): al guardar, si cambió el equipo o las fechas y
//     hay cruces, pide confirmación («Guardar igual»).
// CUÁNDO preguntar vive en utils/aseosMismoDia.ts; QUÉ es «mismo día» y «le
// toca», en el backend (backend/src/utils/aseosMismoDia.js). Acá no se recalcula
// ninguna de las dos cosas.

import React, { useEffect, useState } from "react";
import { Alert, Form } from "antd";
import type { FormInstance } from "antd";
import {
  aseosApi,
  type AseoMismoDia,
  type CruceMismoDia,
} from "../../../services/aseos.api";
import { getModal } from "../../../utils/antdStatic";
import { formatPropertyName } from "../../../utils/propertyDisplay";
import {
  consultaMismoDia,
  mismaConsultaMismoDia,
  type ConsultaMismoDia,
} from "../../../utils/aseosMismoDia";

const ESTADOS: Record<string, string> = {
  pendiente: "pendiente",
  en_proceso: "en proceso",
  completado: "completado",
  pagado: "pagado",
};

/** YYYY-MM-DD → DD-MM-YYYY, sin pasar por Date (nada de corrimientos de zona). */
const fechaCorta = (iso: string | null): string =>
  iso && /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso.split("-").reverse().join("-") : "sin fecha";

function describirAseo(a: AseoMismoDia): string {
  const dias =
    a.fechaTermino && a.fechaTermino !== a.fechaInicio
      ? `del ${fechaCorta(a.fechaInicio)} al ${fechaCorta(a.fechaTermino)}`
      : fechaCorta(a.fechaInicio);
  const hora = a.horaAseo ? ` a las ${a.horaAseo}` : "";
  const estado = a.estado ? ` · ${ESTADOS[a.estado] ?? a.estado}` : "";
  const propiedad = formatPropertyName(a.propiedad) || "Propiedad sin nombre";
  return `${propiedad} — ${dias}${hora}${estado} (Aseo #${a.id})`;
}

/** «Fulana ya tiene otro aseo: …» — la comparten el aviso y la confirmación. */
export const ListaAseosMismoDia: React.FC<{ cruces: CruceMismoDia[] }> = ({ cruces }) => (
  <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
    {cruces.map((c) => (
      <li key={c.usuarioId}>
        <strong>{c.nombre}</strong>{" "}
        {c.aseos.length === 1 ? "ya tiene otro aseo:" : `ya tiene ${c.aseos.length} aseos más:`}
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {c.aseos.map((a) => (
            <li key={a.id}>{describirAseo(a)}</li>
          ))}
        </ul>
      </li>
    ))}
  </ul>
);

/**
 * Cruces para la consulta. Espera un momento antes de preguntar (el admin suele
 * elegir varias aseadoras seguidas) y descarta las respuestas viejas.
 */
function useAseosMismoDia(consulta: ConsultaMismoDia | null): CruceMismoDia[] {
  const [cruces, setCruces] = useState<CruceMismoDia[]>([]);
  // La consulta es un objeto nuevo en cada render: la clave la resume.
  const clave = consulta ? JSON.stringify(consulta) : "";
  useEffect(() => {
    // Lo que se muestra corresponde SIEMPRE al equipo y las fechas actuales: al
    // cambiar la consulta se limpia antes de volver a preguntar.
    setCruces([]);
    if (!consulta) return;
    let vigente = true;
    const timer = setTimeout(async () => {
      const res = await aseosApi.mismoDia(consulta);
      if (vigente) setCruces(res?.data ?? []);
    }, 400);
    return () => {
      vigente = false;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave]);
  return cruces;
}

interface AvisoProps {
  form: FormInstance;
  /** Equipo elegido, responsable primero. */
  usuarioIds: number[];
  /** Nombres de los campos de fecha del form (Dayjs o YYYY-MM-DD). */
  campoDesde: string;
  campoHasta: string;
  /** Aseo que se está editando: no cuenta como cruce consigo mismo. */
  excluirAseoId?: number | null;
}

export const AvisoAseosMismoDia: React.FC<AvisoProps> = ({
  form,
  usuarioIds,
  campoDesde,
  campoHasta,
  excluirAseoId,
}) => {
  const desde = Form.useWatch(campoDesde, form);
  const hasta = Form.useWatch(campoHasta, form);
  const cruces = useAseosMismoDia(
    consultaMismoDia({ usuarioIds, desde, hasta, excluirAseoId }),
  );
  if (cruces.length === 0) return null;
  return (
    <Alert
      type="warning"
      showIcon
      style={{ marginBottom: 16 }}
      message="Atención: aseos el mismo día"
      description={
        <>
          <ListaAseosMismoDia cruces={cruces} />
          <div style={{ marginTop: 6 }}>
            Revisa los horarios para que no se topen. Puedes guardar igual.
          </div>
        </>
      }
    />
  );
};

/**
 * Confirmación al guardar. Pregunta solo si hay cruces Y cambió lo que los
 * decide —el equipo o las fechas— respecto de `original` (lo guardado; `null`
 * al crear): editar otra cosa de un aseo que ya tenía el cruce no vuelve a
 * preguntar (el aviso en vivo lo sigue mostrando). Consulta fresca, porque el
 * aviso en vivo puede ir unos instantes atrasado. Si la consulta falla, deja
 * guardar: el aviso es una ayuda, no un requisito.
 *
 * @returns `true` para seguir guardando; `false` si el admin prefirió volver.
 */
export async function confirmarAseosMismoDia(
  consulta: ConsultaMismoDia | null,
  original: ConsultaMismoDia | null = null,
): Promise<boolean> {
  if (!consulta || mismaConsultaMismoDia(consulta, original)) return true;
  const res = await aseosApi.mismoDia(consulta);
  const cruces = res?.data ?? [];
  const modal = getModal();
  if (cruces.length === 0 || !modal) return true;
  return new Promise<boolean>((resolve) => {
    modal.confirm({
      title: "Aseos el mismo día",
      width: 560,
      content: (
        <div>
          <ListaAseosMismoDia cruces={cruces} />
          <p style={{ margin: "8px 0 0" }}>
            Revisa los horarios para que no se topen. ¿Quieres guardar igual?
          </p>
        </div>
      ),
      okText: "Guardar igual",
      cancelText: "Volver a revisar",
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });
}
